import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Boxes, ChevronRight } from "lucide-react";
import { Page, PageHeader, Section } from "@/app/Page";
import { useWorkspaceSlug } from "@/app/context";
import { Badge, Card, EmptyState, ErrorState, ExplainPanel, Money, ShowAsCode, Skeleton } from "@/design";
import { useBilling, useInvoices, useProjects, useSpendCaps, useWorkspaceSpend, type SpendSummary } from "@/lib/api";
import { docsUrl } from "@/lib/config";
import { moneyAmount } from "@/lib/format";
import { buildModel, ON_CAP, pounds } from "./$project/-spend/model";
import { Invoices } from "./$project/-spend/Sections";
import { SpendHero } from "./$project/-spend/SpendHero";
import { UsageSection } from "./$project/-spend/UsageSection";

export const Route = createFileRoute("/$workspace/spend")({
  staticData: { title: "Spend" },
  component: WorkspaceSpendScreen,
});

/** Spend across every project in the workspace: the cap, usage over time, and which project costs what. */
function WorkspaceSpendScreen() {
  const workspace = useWorkspaceSlug();
  const spend = useWorkspaceSpend(workspace);
  const caps = useSpendCaps(workspace);
  const invoices = useInvoices(workspace);
  const billing = useBilling(workspace);

  const error = spend.error ?? caps.error;
  const m = spend.data && caps.data ? buildModel(spend.data, caps.data.data, null) : null;

  return (
    <Page width="wide">
      <PageHeader
        title="Spend"
        description="What the whole workspace costs this month, project by project, and the cap that keeps it in check."
        actions={
          <ShowAsCode
            title="Check workspace spend from your terminal or agent"
            cli={"guv spend --forecast\nguv spend cap list"}
            apiCall={{ method: "GET", path: `/v1/workspaces/${workspace}/spend` }}
          />
        }
      />

      {error ? (
        <ErrorState
          error={error}
          onRetry={() => {
            void spend.refetch();
            void caps.refetch();
          }}
        />
      ) : !m || !spend.data ? (
        <div role="status" aria-busy="true" className="grid gap-5 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
          <span className="sr-only">Loading spend</span>
          <Card className="p-6">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="mt-4 h-10 w-3/4" />
            <Skeleton className="mt-6 h-2.5 w-full" />
            <Skeleton className="mt-6 h-[190px] w-full" />
          </Card>
          <Card className="p-6">
            <Skeleton className="h-5 w-28" />
            <Skeleton className="mt-4 h-40 w-full" />
          </Card>
        </div>
      ) : (
        <>
          <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
            <SpendHero m={m} updatedAt={spend.data.updated_at} />
            <ByProject summary={spend.data} workspace={workspace} capLabel={m.capRecord ? ON_CAP[m.capRecord.on_cap].short : null} cap={m.cap} />
          </div>

          <UsageSection className="mt-10" scope={{ type: "workspace", workspace }} />

          <Section className="mt-10" title="Invoices" description="One invoice a month for the whole workspace.">
            <Invoices
              invoices={invoices.data?.data}
              isPending={invoices.isPending}
              error={invoices.error}
              onRetry={() => void invoices.refetch()}
              nextEstimate={billing.data?.next_invoice_estimate ? moneyAmount(billing.data.next_invoice_estimate) : undefined}
              workspace={workspace}
            />
          </Section>
        </>
      )}
    </Page>
  );
}

function ByProject({ summary, workspace, cap, capLabel }: { summary: SpendSummary; workspace: string; cap: number | null; capLabel: string | null }) {
  const projects = useProjects(workspace);
  const items = (summary.breakdown ?? []).filter((b) => b.type === "project");
  const max = Math.max(0.01, ...items.map((i) => moneyAmount(i.cost)));
  const slugOf = (id: string) => projects.data?.data.find((p) => p.id === id)?.slug;

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 px-5 pb-2 pt-4">
        <div>
          <h2 className="text-md font-semibold text-text">By project</h2>
          <p className="mt-0.5 text-sm text-muted">This month so far.</p>
        </div>
        <Link to="/$workspace/settings" params={{ workspace }} hash="spend-cap" className="inline-flex items-center gap-1 text-sm font-medium text-accent-text hover:underline">
          Caps and billing <ArrowUpRight className="size-3.5" />
        </Link>
      </div>
      {!items.length ? (
        <EmptyState size="sm" icon={<Boxes />} title="No projects yet" description="Create a project and deploy an app; its spend shows up here within a few minutes." />
      ) : (
        <ul className="divide-y divide-border">
          {[...items]
            .sort((a, b) => moneyAmount(b.cost) - moneyAmount(a.cost))
            .map((it) => {
              const slug = slugOf(it.id);
              const cost = moneyAmount(it.cost);
              const body = (
                <>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="truncate font-medium text-text">{it.name}</span>
                      <Money value={it.cost} className="font-medium text-text" />
                    </div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-3" aria-hidden="true">
                        <div className="h-full rounded-full bg-chart-1" style={{ width: `${Math.max(2, (cost / max) * 100)}%` }} />
                      </div>
                      <span className="w-9 text-right text-xs text-subtle tabular">{Math.round(it.share_percent ?? 0)}%</span>
                    </div>
                  </div>
                  {slug && <ChevronRight className="size-4 shrink-0 text-subtle" aria-hidden="true" />}
                </>
              );
              return (
                <li key={it.id}>
                  {slug ? (
                    <Link
                      to="/$workspace/$project/spend"
                      params={{ workspace, project: slug }}
                      className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-surface-2/60 focus-visible:bg-surface-2/60"
                      aria-label={`${it.name}: ${pounds(Number(cost.toFixed(2)))} this month, ${Math.round(it.share_percent ?? 0)}% of the workspace. Open its spend.`}
                    >
                      {body}
                    </Link>
                  ) : (
                    <div className="flex items-center gap-3 px-5 py-3">{body}</div>
                  )}
                </li>
              );
            })}
        </ul>
      )}
      {cap != null && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-5 py-3 text-sm text-muted">
          <span>
            Workspace cap <Money value={cap} whole className="font-medium text-text" />
          </span>
          {capLabel && (
            <Badge size="sm" variant="outline">
              At the cap: {capLabel.toLowerCase()}
            </Badge>
          )}
          <ExplainPanel title="Workspace and project caps" learnMoreUrl={docsUrl("/spend#caps")}>
            <p>The workspace cap covers every project together. A project can also have its own, smaller cap on its Spend page.</p>
            <p>Whichever cap is reached first applies.</p>
          </ExplainPanel>
        </div>
      )}
    </Card>
  );
}
